import { useCallback, useEffect, useRef, useState } from "react";
import { getOrder } from "@/services/student/checkout/checkout.service";
import type { OrderStatus } from "@/services/student/checkout/checkout.interfaces";

const PAID_STATUSES: OrderStatus[] = ["paid", "completed"];
const FAILED_STATUSES: OrderStatus[] = ["failed", "cancelled"];

interface UseOrderPaymentStatusOptions {
  orderId: string | null;
  enabled: boolean;
  intervalMs?: number;
  timeoutMs?: number;
  onPaid?: () => void;
  onFailed?: () => void;
  onTimeout?: () => void;
}

export const useOrderPaymentStatus = ({
  orderId,
  enabled,
  intervalMs = 3000,
  timeoutMs = 180000,
  onPaid,
  onFailed,
  onTimeout,
}: UseOrderPaymentStatusOptions) => {
  const [status, setStatus] = useState<OrderStatus | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [timedOut, setTimedOut] = useState(false);

  const callbacksRef = useRef({ onPaid, onFailed, onTimeout });
  callbacksRef.current = { onPaid, onFailed, onTimeout };

  const cancelledRef = useRef(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | undefined>();
  const startedAtRef = useRef<number>(0);
  const pollFnRef = useRef<() => Promise<void>>(async () => {});

  const startPolling = useCallback(() => {
    if (!orderId) return;
    cancelledRef.current = false;
    startedAtRef.current = Date.now();
    setTimedOut(false);
    setIsPolling(true);

    if (intervalRef.current) clearInterval(intervalRef.current);

    const poll = async () => {
      try {
        const response = await getOrder(orderId);
        if (cancelledRef.current || !response.success || !response.order) return;
        const nextStatus = response.order.status;
        setStatus(nextStatus);
        if (PAID_STATUSES.includes(nextStatus)) {
          cancelledRef.current = true;
          setIsPolling(false);
          if (intervalRef.current) clearInterval(intervalRef.current);
          callbacksRef.current.onPaid?.();
          return;
        }
        if (FAILED_STATUSES.includes(nextStatus)) {
          cancelledRef.current = true;
          setIsPolling(false);
          if (intervalRef.current) clearInterval(intervalRef.current);
          callbacksRef.current.onFailed?.();
        }
      } catch {
        // keep polling on transient errors
      }
    };
    pollFnRef.current = poll;

    void poll();
    intervalRef.current = setInterval(() => {
      if (Date.now() - startedAtRef.current >= timeoutMs) {
        cancelledRef.current = true;
        setIsPolling(false);
        setTimedOut(true);
        if (intervalRef.current) clearInterval(intervalRef.current);
        callbacksRef.current.onTimeout?.();
        return;
      }
      void poll();
    }, intervalMs);
  }, [orderId, intervalMs, timeoutMs]);

  useEffect(() => {
    if (!enabled || !orderId) {
      setIsPolling(false);
      return;
    }
    startPolling();

    const handleVisibility = () => {
      if (document.visibilityState === "visible" && !cancelledRef.current) {
        void pollFnRef.current();
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      cancelledRef.current = true;
      if (intervalRef.current) clearInterval(intervalRef.current);
      setIsPolling(false);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [enabled, orderId, startPolling]);

  const refetch = useCallback(() => {
    if (!orderId) return;
    startPolling();
  }, [orderId, startPolling]);

  return { status, isPolling, timedOut, refetch };
};
